/* Single rule for visible editorial reviews: if text is clipped, offer an
   accessible, persistent More/Less toggle on phone, iPad and desktop. */
(() => {
  const selectors = [
    '.sf-review-carousel .sf-review-preview-card .sf-review-excerpt',
    '.sf-reviews-page .sf-editorial-review .sf-review-excerpt',
    '.sf-gallery-reviews [data-sf-gallery-quotes] > .sf-gallery-quote'
  ].join(',');
  const key = 'sfReviewOverflowManaged';
  const isVisible = node => Boolean(node.getClientRects().length && node.getBoundingClientRect().width > 0);
  const setText = (button,expanded) => {
    const label=expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';
    let target=button.querySelector('.sf-review-more-label,.sf-review-overflow-label');
    if (!target) {
      const first=button.firstChild;
      if (first?.nodeType === Node.TEXT_NODE && first.textContent !== label+' ') first.textContent=label+' ';
      else {
        target=document.createElement('span');
        target.className='sf-review-overflow-label';
        button.prepend(target);
      }
    }
    if (target && target.textContent !== label) target.textContent=label;
    // A previous button can contain both its own literal "Mehr anzeigen" text
    // and a new label span. Remove ONLY duplicate More/Less text inside it.
    if (target) {
      Array.from(button.childNodes).filter(node=>
        node!==target && node.nodeType===Node.TEXT_NODE &&
        /^\s*(Mehr anzeigen|Weniger anzeigen)\s*$/i.test(node.textContent||'')
      ).forEach(node=>node.remove());
      // Older buttons may wrap a second label in an unnamed span.
      button.querySelectorAll('span').forEach(node => {
        if(node!==target && !node.contains(target) &&
           /^\s*(Mehr anzeigen|Weniger anzeigen)\s*$/i.test(node.textContent||'')) node.remove();
      });
    }
    button.setAttribute('aria-expanded',String(expanded));
    button.setAttribute('aria-label',expanded ? 'Weniger von dieser Rezension anzeigen' : 'Rezension vollständig anzeigen');
  };

  const makeButton = host => {
    // Check the WHOLE review, not just the excerpt: older templates sometimes
    // put their own button in a sibling wrapper. Keep precisely one control.
    const card=host.closest('.sf-review-preview-card,.sf-editorial-review,.sf-gallery-quote') || host;
    const selector='button.sf-review-inline-more,button.sf-review-read-more,button.sf-review-demo-more,button.sf-review-overflow-toggle';
    const existing=Array.from(card.querySelectorAll('button,a,[role="button"]'))
      .filter(node => node.matches(selector) || /^(Mehr anzeigen|Weniger anzeigen)\s*$/i.test((node.textContent||'').trim()))
      .filter(node => node.closest('.sf-review-preview-card,.sf-editorial-review,.sf-gallery-quote') === card);
    let button=existing.find(node=>!node.classList.contains('sf-review-overflow-toggle')) || existing[0];
    if (button) {
      existing.forEach(other=>{if(other!==button)other.remove();});
      button.classList.add('sf-review-inline-more');
      if(button.parentElement!==host)host.append(button);
      return button;
    }
    button=document.createElement('button');
    button.type='button';
    button.className='sf-review-read-more sf-review-inline-more sf-review-overflow-toggle';
    button.hidden=true;
    const label=document.createElement('span');
    label.className='sf-review-overflow-label';
    label.textContent='Mehr anzeigen';
    const arrow=document.createElement('span');
    arrow.className='sf-review-chevron';
    arrow.setAttribute('aria-hidden','true');
    button.append(label,arrow);
    host.append(button);
    return button;
  };

  const measure = host => {
    if (!host.isConnected || !isVisible(host)) return;
    const quote=host.querySelector(':scope > blockquote');
    if (!quote || !isVisible(quote)) return;
    const button=makeButton(host);
    host.classList.add('sf-review-overflow-managed');
    const wasExpanded=host.classList.contains('sf-review-expanded');
    if (wasExpanded) host.classList.remove('sf-review-expanded');

    // Measure the true folded height, then the unrestricted text height.
    const shortHeight=quote.getBoundingClientRect().height;
    const saved=quote.getAttribute('style');
    const override={
      display:'block',height:'auto','max-height':'none',
      overflow:'visible','-webkit-line-clamp':'unset',
      '-webkit-mask-image':'none','mask-image':'none',filter:'none'
    };
    Object.entries(override).forEach(([prop,val])=>quote.style.setProperty(prop,val,'important'));
    const fullHeight=quote.getBoundingClientRect().height;
    if (saved === null) quote.removeAttribute('style');
    else quote.setAttribute('style',saved);

    const hasMore=fullHeight > shortHeight + 2;
    const expanded=hasMore && wasExpanded;
    host.classList.toggle('sf-review-actually-truncated',hasMore);
    host.classList.toggle('sf-review-expanded',expanded);
    if (button.hidden === hasMore) button.hidden=!hasMore;
    setText(button,expanded);
  };

  let scheduled=false;
  const refresh=()=>{
    scheduled=false;
    document.querySelectorAll(selectors).forEach(measure);
  };
  const schedule=()=>{
    if (scheduled) return;
    scheduled=true;
    requestAnimationFrame(refresh);
  };

  // Capture overrides legacy click listeners that previously hid the button
  // or kept an old max-height after opening. Only review toggles are affected.
  document.addEventListener('click',event=>{
    const button=event.target.closest?.('.sf-review-inline-more');
    const host=button?.closest('.sf-review-excerpt,.sf-gallery-quote');
    if (!host || !host.matches(selectors)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.hidden || !host.classList.contains('sf-review-actually-truncated')) return;
    const expanded=!host.classList.contains('sf-review-expanded');
    host.classList.toggle('sf-review-expanded',expanded);
    const quote=host.querySelector(':scope > blockquote');
    if (quote) {
      // Old scripts sometimes leave an inline height behind.
      ['display','height','max-height','overflow','-webkit-line-clamp','-webkit-mask-image','mask-image','filter']
        .forEach(prop=>quote.style.removeProperty(prop));
    }
    setText(button,expanded);
    // Keep the control available in BOTH states; recalibrate after reflow.
    button.hidden=false;
    schedule();
  },true);

  const observer=new MutationObserver(mutations=>{
    if (mutations.some(change=>change.addedNodes.length || change.removedNodes.length)) schedule();
  });
  const init=()=>{
    schedule();
    observer.observe(document.body,{subtree:true,childList:true});
  };
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
  window.addEventListener('resize',schedule,{passive:true});
  window.addEventListener('load',schedule,{once:true});
  document.fonts?.ready?.then(schedule);
  document.addEventListener('sf:reviews-updated',schedule);
  document.addEventListener('sf:book-open',schedule);
  document.addEventListener('shopify:section:load',schedule);
  document.addEventListener('click',event=>{
    if (event.target.closest?.('[data-sf-review-dot],[data-sf-review-more],[data-sf-zoom-open]')) {
      requestAnimationFrame(()=>requestAnimationFrame(schedule));
    }
  });
})();